import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { MAX_FILE_SIZE, MAX_FILES, MEDIA_EXT } from "@/lib/constants";

const isMedia = (file) => /^(image|video)\//.test(file.type) || MEDIA_EXT.test(file.name);
const isVideo = (file) => file.type.startsWith("video/") || /\.(mp4|mov|m4v|avi|mkv|webm|3gp|3g2|wmv|flv|mpe?g|ts)$/i.test(file.name);
const newKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export function useAttachments() {
  const [items, setItems] = useState([]);
  const countRef = useRef(0);
  countRef.current = items.length;

  const update = (key, patch) => setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...patch } : i)));

  const uploadOne = async (item) => {
    const body = new FormData();
    body.append("file", item.file);
    try {
      const { data } = await api.post("/uploads", body, {
        onUploadProgress: (e) => e.total && update(item.key, { progress: Math.min(99, Math.round((e.loaded * 100) / e.total)) }),
      });
      update(item.key, { status: "done", progress: 100, id: data.id });
      toast.success("Lampiran berhasil diunggah", { description: item.name });
    } catch (err) {
      const message = formatApiError(err, "Gagal mengunggah lampiran");
      update(item.key, { status: "error", error: message });
      toast.error("Gagal mengunggah lampiran", { description: `${item.name}: ${message}` });
    }
  };

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    const slots = MAX_FILES - countRef.current;
    if (slots <= 0) return toast.warning(`Maksimal ${MAX_FILES} lampiran per pengaduan`);
    if (files.length > slots) toast.warning(`Hanya ${slots} file pertama yang diambil (maks. ${MAX_FILES} lampiran)`);
    const accepted = [];
    files.slice(0, slots).forEach((file) => {
      if (!isMedia(file)) return toast.error(`${file.name}: format tidak didukung (hanya foto/video)`);
      if (file.size > MAX_FILE_SIZE) return toast.error(`${file.name}: ukuran melebihi 25MB`);
      accepted.push({
        key: newKey(), file, name: file.name, size: file.size, kind: isVideo(file) ? "video" : "image",
        previewUrl: URL.createObjectURL(file), progress: 0, status: "uploading",
      });
    });
    if (!accepted.length) return;
    countRef.current += accepted.length;
    setItems((prev) => [...prev, ...accepted]);
    accepted.forEach(uploadOne);
  };

  const remove = (key) =>
    setItems((prev) => {
      const target = prev.find((i) => i.key === key);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((i) => i.key !== key);
    });

  const reset = useCallback(() => {
    setItems((prev) => {
      prev.forEach((i) => i.previewUrl && URL.revokeObjectURL(i.previewUrl));
      return [];
    });
  }, []);

  const active = items.filter((i) => i.status === "uploading");
  const progress = active.length ? Math.round(active.reduce((s, i) => s + i.progress, 0) / active.length) : 0;

  return {
    items, addFiles, remove, reset, progress,
    uploading: active.length > 0,
    readyIds: items.filter((i) => i.status === "done").map((i) => i.id),
  };
}
